import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactElement,
} from 'react';
import VisibilityObserver from '../../base-components/visibility-observer/VisibilityObserver';

interface SequentialSelector {
  children: ReactElement[];
  id: string;
  completed: boolean[];
}

function SequentialSelectorCmp({
  children,
  id,
  completed,
}: Readonly<SequentialSelector>) {
  const ref = useRef<HTMLDivElement>(null);
  const childCount = React.Children.count(children);

  const [currentIndex, setCurrentIndex] = useState(0);

  const getStepperId = (step: number) => 'setpper-' + step;

  const moveToNextStep = useCallback((step: number) => {
    document
      .getElementById(getStepperId(step + 1))
      ?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }, []);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    let startX = 0;
    let startY = 0;

    const onTouchStart = (event: TouchEvent) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    };

    const onTouchMove = (event: TouchEvent) => {
      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (Math.abs(dy) <= 5 && Math.abs(dx) >= 5) {
        if (dx > 0) {
          document
            .getElementById(getStepperId(currentIndex - 1))
            ?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        } else if (completed[currentIndex]) {
          document
            .getElementById(getStepperId(currentIndex + 1))
            ?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
      }
    };

    container.addEventListener('touchstart', onTouchStart);
    container.addEventListener('touchmove', onTouchMove);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
    };
  }, [completed, currentIndex]);

  const containerClass =
    'flex overflow-hidden snap-x snap-mandatory scroll-smooth [-webkit-overflow-scrolling:touch]';

  return (
    <div ref={ref} id={id} className={containerClass}>
      {React.Children.map(children, (child, index) => (
        <div key={getStepperId(index)} id={getStepperId(index)}>
          <VisibilityObserver
            on_fully_visible={() => setCurrentIndex(index)}
            intersection_ratio={1}
            threshold={1.0}
          >
            {React.cloneElement(child, {
              move_to_next_step: () => moveToNextStep(index),
            })}
          </VisibilityObserver>
        </div>
      ))}
    </div>
  );
}

export default SequentialSelectorCmp;
